import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import './Home.css';

const Home = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = useState('monthly');

  useEffect(() => {
    // Redirect authenticated users to their dashboard
    if (isAuthenticated()) {
      if (isAdmin()) {
        navigate('/admin/dashboard');
      } else if (isManager()) {
        navigate('/manager/dashboard');
      }
    }
  }, [isAuthenticated, isAdmin, isManager, navigate]);

  const features = [
    {
      image: '/images/features/qr-code-menus.png',
      title: t('home.features.qrCodeMenus.title'),
      description: t('home.features.qrCodeMenus.description'),
      color: 'primary'
    },
    {
      image: '/images/features/menu-management.jpg',
      title: t('home.features.menuManagement.title'),
      description: t('home.features.menuManagement.description'),
      color: 'accent'
    },
    {
      image: '/images/features/easy-ordering.jpg',
      title: t('home.features.easyOrdering.title'),
      description: t('home.features.easyOrdering.description'),
      color: 'success'
    },
    {
      image: '/images/features/real-time-tracking.jpg',
      title: t('home.features.realTimeTracking.title'),
      description: t('home.features.realTimeTracking.description'),
      color: 'info'
    },
    {
      image: '/images/features/multi-location.jpg',
      title: t('home.features.multiLocation.title'),
      description: t('home.features.multiLocation.description'),
      color: 'warning'
    },
    {
      image: '/images/features/multi-currency.jpg',
      title: t('home.features.multiCurrency.title'),
      description: t('home.features.multiCurrency.description'),
      color: 'primary'
    },
    {
      image: '/images/features/lightning-fast.jpg',
      title: t('home.features.lightningFast.title'),
      description: t('home.features.lightningFast.description'),
      color: 'primary'
    }
  ];

  const stats = [
    { value: '50%', label: t('home.stats.fasterOrdering') },
    { value: '0', label: t('home.stats.appDownloads') },
    { value: '24/7', label: t('home.stats.menuAccess') },
    { value: '100%', label: t('home.stats.contactless') }
  ];

  // Pricing plans. Adding more plans later is just pushing entries onto this array.
  // Arabic shows Omani Rial pricing; other locales keep USD. The currency symbol
  // itself is locale-driven via the 'home.pricing.currency' i18n key.
  const isArabic = i18n.language === 'ar';
  const plans = [
    {
      id: 'starter',
      nameKey: 'home.pricing.starter.name',
      taglineKey: 'home.pricing.starter.tagline',
      monthly: isArabic ? 10 : 29,
      yearly: isArabic ? 100 : 290,
      featureKeys: [
        'home.pricing.starter.feature1',
        'home.pricing.starter.feature2',
        'home.pricing.starter.feature3',
        'home.pricing.starter.feature4',
        'home.pricing.starter.feature5',
        'home.pricing.starter.feature6'
      ],
      ctaTo: '/register',
      ctaKey: 'home.pricing.starter.cta',
      highlighted: true
    }
  ];

  return (
    <div className="home-page fade-in">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            {t('home.hero.badge')}
          </div>
          <h1 className="hero-title">
            {t('home.hero.title')}
            <span className="gradient-text">{t('home.hero.titleHighlight')}</span>
          </h1>
          <p className="hero-description">
            {t('home.hero.description')}
          </p>
          <div className="hero-actions">
            {isAuthenticated() ? (
              <>
                {isAdmin() && (
                  <Link to="/admin/dashboard" className="btn btn-primary btn-lg">
                    {t('home.hero.ctaDashboard')}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </Link>
                )}
                {isManager() && (
                  <Link to="/manager/dashboard" className="btn btn-primary btn-lg">
                    {t('home.hero.ctaDashboard')}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link to="/register" className="btn btn-primary btn-lg">
                  {t('home.hero.ctaGetStarted')}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
                <Link to="/menu/9?mode=demo" className="btn btn-secondary btn-lg btn-demo-cta">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polygon points="10 8 16 12 10 16 10 8"/>
                  </svg>
                  {t('home.hero.ctaTryDemo')}
                </Link>
              </>
            )}
          </div>
        </div>
        
        <div className="hero-visual">
          <div className="hero-image hero-image--photo" aria-hidden="true">
            <img
              src="/images/login-hero.png"
              alt=""
              className="hero-image-photo"
              loading="lazy"
              onError={(e) => {
                const card = e.currentTarget.closest('.hero-image');
                if (card) card.style.display = 'none';
              }}
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <span className="section-eyebrow">01</span>
          <h2>{t('home.features.sectionTitle')}</h2>
          <p>{t('home.features.sectionSubtitle')}</p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className={`feature-card feature-${feature.color}`}>
              <div className="feature-icon">
                <img src={feature.image} alt={feature.title} loading="lazy" />
              </div>
              <div className="feature-card-inner">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="section-header">
          <span className="section-eyebrow">02</span>
          <h2>{t('home.pricing.title')}</h2>
          <p>{t('home.pricing.subtitle')}</p>
        </div>

        <div className="pricing-toggle" role="tablist" aria-label={t('home.pricing.toggleAria')}>
          <button
            type="button"
            role="tab"
            aria-selected={billingInterval === 'monthly'}
            className={`pricing-toggle-option ${billingInterval === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingInterval('monthly')}
          >
            {t('home.pricing.monthly')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={billingInterval === 'yearly'}
            className={`pricing-toggle-option ${billingInterval === 'yearly' ? 'active' : ''}`}
            onClick={() => setBillingInterval('yearly')}
          >
            {t('home.pricing.yearly')}
            <span className="pricing-save-badge">{t('home.pricing.savePercent', { percent: 17 })}</span>
          </button>
        </div>

        <div className="pricing-grid">
          {plans.map((plan) => {
            const price = billingInterval === 'yearly' ? plan.yearly : plan.monthly;
            const periodKey = billingInterval === 'yearly' ? 'home.pricing.perYear' : 'home.pricing.perMonth';
            return (
              <div
                key={plan.id}
                className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}
              >
                {plan.highlighted && (
                  <div className="pricing-badge">{t('home.pricing.popular')}</div>
                )}
                <h3 className="pricing-name">{t(plan.nameKey)}</h3>
                <p className="pricing-tagline">{t(plan.taglineKey)}</p>
                <div className="pricing-price">
                  <span className="pricing-currency">{t('home.pricing.currency')}</span>
                  <span className="pricing-amount">{price}</span>
                  <span className="pricing-period">{t(periodKey)}</span>
                </div>
                {billingInterval === 'yearly' && (
                  <p className="pricing-yearly-note">
                    {t('home.pricing.yearlyEquivalent', { amount: Math.round(plan.yearly / 12) })}
                  </p>
                )}
                <ul className="pricing-features">
                  {plan.featureKeys.map((key) => (
                    <li key={key}>
                      <span className="pricing-check" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </span>
                      {t(key)}
                    </li>
                  ))}
                </ul>
                <Link to={plan.ctaTo} className="btn btn-primary btn-lg pricing-cta">
                  {t(plan.ctaKey)}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </Link>
                <p className="pricing-trial-note">{t('home.pricing.trialNote')}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>{t('home.cta.title')}</h2>
          <p>{t('home.cta.subtitle')}</p>
          <div className="cta-actions">
            <Link to="/register" className="btn btn-primary btn-lg">
              {t('home.cta.button')}
            </Link>
            <Link to="/menu/9?mode=demo" className="btn btn-ghost btn-lg">
              {t('home.cta.demoButton')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="footer-logo">
              <img
                src="/mesoon-logo.svg"
                alt=""
                width="32"
                height="32"
                style={{ display: 'block' }}
              />
            </div>
            <span>{t('brand.name')}</span>
          </div>
          <p className="footer-text">
            {t('home.footer.description')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
